import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "../lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { X, Save, Building2, Mail, Banknote, CalendarClock, Wrench, MapPin, Receipt, AlertTriangle, Store } from "lucide-react";
import SupplierBranchOpsTab from "./SupplierBranchOpsTab";

const BRANCH_CODES = ["HMO", "XAL", "PVR", "NVR", "CRP"];
const MODALITIES = ["por_visita", "contrato_mensual", "por_evento", "cotizacion_individual"];
const RESPONSE_TIMES = ["24h", "48h", "72h", "semanal", "mensual"];

const branchCodeOf = (b) => (b.code || (b.pos_branch_code || "").split("-")[0] || "").toUpperCase();
const norm = (v) => (v === null || v === undefined ? "" : String(v));

export default function SupplierEditModal({ supplier, branches, onClose, onSaved, initialTab = "identity" }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(initialTab);
  const [busy, setBusy] = useState(false);
  const [regimes, setRegimes] = useState({});
  const [activeOcCount, setActiveOcCount] = useState(0);
  const [confirmFiscal, setConfirmFiscal] = useState(false);
  const [rateError, setRateError] = useState("");

  useEffect(() => {
    api.get("/sat-tax-regimes").then((r) => setRegimes(r.data?.regimes || {})).catch(() => {});
    api.get("/purchase-orders").then((r) => {
      const active = (r.data || []).filter(
        (po) => po.supplier_id === supplier.id &&
          ["pending_approval", "approved", "received"].includes(po.status)
      );
      setActiveOcCount(active.length);
    }).catch(() => {});
  }, [supplier.id]);

  const pct = (v) => (v === null || v === undefined || v === "" ? "" : String(round4(Number(v) * 100)));
  const round4 = (n) => Math.round(n * 10000) / 10000;

  const originalCodes = useMemo(() => {
    const byId = Object.fromEntries(branches.map((b) => [b.id, b]));
    return (supplier.branches_authorized || [])
      .map((id) => (byId[id] ? branchCodeOf(byId[id]) : null))
      .filter(Boolean)
      .sort();
  }, [supplier, branches]);

  const [form, setForm] = useState(() => ({
    business_name: norm(supplier.business_name),
    commercial_name: norm(supplier.commercial_name),
    supplier_type: supplier.supplier_type || "insumos",
    category_hint: norm(supplier.category_hint),
    website: norm(supplier.website),
    contact_name: norm(supplier.contact_name),
    contact_phone: norm(supplier.contact_phone),
    contact_email: norm(supplier.contact_email),
    billing_contact_name: norm(supplier.billing_contact_name),
    billing_contact_phone: norm(supplier.billing_contact_phone),
    billing_contact_email: norm(supplier.billing_contact_email),
    credit_days: norm(supplier.credit_days),
    has_credit: !!supplier.has_credit,
    payment_conditions: norm(supplier.payment_conditions),
    delivery_time_description: norm(supplier.delivery_time_description),
    order_period: norm(supplier.order_period),
    order_days_description: norm(supplier.order_days_description),
    order_day_specific: norm(supplier.order_day_specific),
    delivery_day_specific: norm(supplier.delivery_day_specific),
    attention_schedule: norm(supplier.attention_schedule),
    operations_start_date: norm(supplier.operations_start_date),
    service_modality: supplier.service_modality || "",
    service_response_time: supplier.service_response_time || "",
    service_description: norm(supplier.service_description),
    has_active_contract: !!supplier.has_active_contract,
    tax_regime: supplier.tax_regime || "",
    isr_pct: pct(supplier.isr_withholding_rate),
    iva_pct: pct(supplier.iva_withholding_rate),
    withholding_notes: norm(supplier.withholding_notes),
  }));

  const [codes, setCodes] = useState(originalCodes);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCode = (c) =>
    setCodes((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c].sort()));

  const isServ = form.supplier_type === "servicios";
  const lbl = (k) => t(`supplierEdit.fields.${k}`);

  const buildPayload = () => {
    const p = {};
    const TEXT = [
      "business_name", "commercial_name", "category_hint", "website",
      "contact_name", "contact_phone", "billing_contact_name", "billing_contact_phone",
      "payment_conditions", "delivery_time_description",
      "order_period", "order_days_description", "order_day_specific", "delivery_day_specific",
      "attention_schedule", "operations_start_date", "service_description",
    ];
    TEXT.forEach((f) => {
      const v = (form[f] || "").trim();
      if (v && v !== norm(supplier[f])) p[f] = v;
    });
    // emails (skip if empty to avoid 422 on EmailStr)
    ["contact_email", "billing_contact_email"].forEach((f) => {
      const v = (form[f] || "").trim().toLowerCase();
      if (v && v !== norm(supplier[f]).toLowerCase()) p[f] = v;
    });
    // number
    const cd = form.credit_days === "" ? null : parseInt(form.credit_days, 10);
    if (cd !== null && !Number.isNaN(cd) && cd !== (supplier.credit_days ?? null)) p.credit_days = cd;
    // booleans
    if (form.has_credit !== !!supplier.has_credit) p.has_credit = form.has_credit;
    if (form.has_active_contract !== !!supplier.has_active_contract) p.has_active_contract = form.has_active_contract;
    // type
    if (form.supplier_type !== (supplier.supplier_type || "insumos")) p.supplier_type = form.supplier_type;
    // service modality / response time
    if (isServ) {
      if (form.service_modality && form.service_modality !== (supplier.service_modality || ""))
        p.service_modality = form.service_modality;
      if (form.service_response_time && form.service_response_time !== (supplier.service_response_time || ""))
        p.service_response_time = form.service_response_time;
    }
    // branches
    if (JSON.stringify(codes) !== JSON.stringify(originalCodes) && codes.length > 0) {
      p.branches_codes = codes;
    }
    // régimen fiscal
    if (form.tax_regime !== (supplier.tax_regime || "")) {
      p.tax_regime = form.tax_regime || null;
    }
    // tasas de retención (UI en %, persistir en decimal 0-1)
    const toRate = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = parseFloat(v);
      return Number.isNaN(n) ? null : Math.round((n / 100) * 1e6) / 1e6;
    };
    const isrRate = toRate(form.isr_pct);
    const ivaRate = toRate(form.iva_pct);
    const origIsr = Number(supplier.isr_withholding_rate || 0);
    const origIva = Number(supplier.iva_withholding_rate || 0);
    if (isrRate !== null && Math.abs(isrRate - origIsr) > 1e-9) p.isr_withholding_rate = isrRate;
    if (ivaRate !== null && Math.abs(ivaRate - origIva) > 1e-9) p.iva_withholding_rate = ivaRate;
    const wn = (form.withholding_notes || "").trim();
    if (wn !== norm(supplier.withholding_notes)) p.withholding_notes = wn || null;
    return p;
  };

  const fiscalKeys = ["tax_regime", "isr_withholding_rate", "iva_withholding_rate", "withholding_notes"];
  const fiscalChanged = (pl) => fiscalKeys.some((k) => k in pl);

  const payload = buildPayload();
  const hasChanges = Object.keys(payload).length > 0;

  const save = async () => {
    if (!hasChanges) {
      toast.info(t("supplierEdit.noChanges"));
      return;
    }
    // Validación cliente de tasas (0-100) antes de enviar: nunca mandamos un payload inválido.
    const checkPct = (v) => {
      if (v === "" || v === null || v === undefined) return true;
      const n = parseFloat(v);
      return !Number.isNaN(n) && n >= 0 && n <= 100;
    };
    if (!checkPct(form.isr_pct) || !checkPct(form.iva_pct)) {
      setRateError(t("supplierEdit.fiscal.rateRangeError"));
      setTab("fiscal");
      toast.error(t("supplierEdit.fiscal.rateRangeError"));
      return;
    }
    setRateError("");
    // Confirmación si cambian retenciones y hay OCs activas (aplican solo a OCs futuras).
    if (fiscalChanged(payload) && activeOcCount > 0 && !confirmFiscal) {
      setConfirmFiscal(true);
      return;
    }
    setBusy(true);
    try {
      await api.put(`/suppliers/${supplier.id}/admin-edit`, payload);
      toast.success(t("supplierEdit.successToast"));
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || t("common.error"))
        : (detail || t("common.error"));
      toast.error(msg);
      setConfirmFiscal(false);
    } finally {
      setBusy(false);
    }
  };

  const TABS = [
    { v: "identity", l: t("supplierEdit.tab.identity"), icon: Building2 },
    { v: "contact", l: t("supplierEdit.tab.contact"), icon: Mail },
    { v: "terms", l: t("supplierEdit.tab.terms"), icon: Banknote },
    { v: "fiscal", l: t("supplierEdit.tab.fiscal"), icon: Receipt },
    { v: "operation", l: t("supplierEdit.tab.operation"), icon: CalendarClock },
    ...(isServ ? [{ v: "services", l: t("supplierEdit.tab.services"), icon: Wrench }] : []),
    { v: "branches", l: t("supplierEdit.tab.branches"), icon: MapPin },
    { v: "branch_ops", l: t("supplierEdit.tab.branchOps"), icon: Store },
  ];

  const field = (k, wide = false) => (
    <div key={k} className={wide ? "sm:col-span-2" : ""}>
      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl(k)}</label>
      <input
        data-testid={`edit-field-${k}`}
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
      />
    </div>
  );

  const toggleField = (k) => (
    <label key={k} className="flex items-center justify-between gap-3 sm:col-span-2 cursor-pointer">
      <span className="text-sm font-medium text-[#1A1A1A]">{lbl(k)}</span>
      <button
        type="button"
        data-testid={`edit-toggle-${k}`}
        onClick={() => set(k, !form[k])}
        className={`relative w-11 h-6 rounded-full transition-colors ${form[k] ? "bg-emerald-500" : "bg-slate-300"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form[k] ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="supplier-edit-modal"
        className="bg-white rounded-2xl border border-slate-200 w-full max-w-[900px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#FFDF00] flex items-center justify-center">
              <Building2 className="w-4.5 h-4.5 text-[#1A1A1A]" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-base text-[#1A1A1A]">{t("supplierEdit.modalTitle")}</div>
              <div className="text-xs text-slate-500 truncate">{supplier.business_name}</div>
            </div>
          </div>
          <button data-testid="supplier-edit-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="px-6 pt-4 flex items-center gap-1 overflow-x-auto">
          {TABS.map((tb) => (
            <button
              key={tb.v}
              data-testid={`edit-tab-${tb.v}`}
              onClick={() => setTab(tb.v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition ${
                tab === tb.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <tb.icon className="w-3.5 h-3.5" /> {tb.l}
            </button>
          ))}
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {tab === "identity" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("business_name")}
              {field("commercial_name")}
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("supplier_type")}</label>
                <select
                  data-testid="edit-field-supplier_type"
                  value={form.supplier_type}
                  onChange={(e) => set("supplier_type", e.target.value)}
                  className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                >
                  <option value="insumos">{t("suppliers.typeInsumos")}</option>
                  <option value="servicios">{t("suppliers.typeServicios")}</option>
                </select>
              </div>
              {field("category_hint")}
              {field("website", true)}
            </div>
          )}

          {tab === "contact" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("contact_name")}
              {field("contact_phone")}
              {field("contact_email", true)}
              {field("billing_contact_name")}
              {field("billing_contact_phone")}
              {field("billing_contact_email", true)}
            </div>
          )}

          {tab === "terms" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("credit_days")}
              <div className="hidden sm:block" />
              {toggleField("has_credit")}
              {field("payment_conditions", true)}
              {field("delivery_time_description", true)}
            </div>
          )}

          {tab === "fiscal" && (
            <div className="space-y-5" data-testid="edit-fiscal-section">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("tax_regime")}</label>
                <select
                  data-testid="edit-field-tax_regime"
                  value={form.tax_regime}
                  onChange={(e) => set("tax_regime", e.target.value)}
                  className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                >
                  <option value="">{t("supplierEdit.fiscal.selectRegime")}</option>
                  {Object.entries(regimes).map(([code, name]) => (
                    <option key={code} value={code}>{code} — {name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("isr_pct")}</label>
                  <div className="mt-1.5 relative">
                    <input
                      data-testid="edit-field-isr_pct"
                      type="number" step="0.0001" min="0" max="100"
                      value={form.isr_pct}
                      onChange={(e) => { set("isr_pct", e.target.value); setRateError(""); }}
                      className="w-full px-3 py-2 pr-8 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("iva_pct")}</label>
                  <div className="mt-1.5 relative">
                    <input
                      data-testid="edit-field-iva_pct"
                      type="number" step="0.0001" min="0" max="100"
                      value={form.iva_pct}
                      onChange={(e) => { set("iva_pct", e.target.value); setRateError(""); }}
                      className="w-full px-3 py-2 pr-8 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  </div>
                </div>
              </div>
              {form.tax_regime === "626" && (parseFloat(form.isr_pct) || 0) === 0 && (
                <div data-testid="edit-resico-warning" className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{t("supplierEdit.fiscal.resicoWarning")}</span>
                </div>
              )}
              {rateError && (
                <div data-testid="edit-rate-error" className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{rateError}</span>
                </div>
              )}
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("withholding_notes")}</label>
                <textarea
                  data-testid="edit-field-withholding_notes"
                  value={form.withholding_notes}
                  onChange={(e) => set("withholding_notes", e.target.value)}
                  rows={2}
                  className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                />
              </div>
              {activeOcCount > 0 && (
                <p className="text-xs text-slate-500">{t("supplierEdit.fiscal.activeOcsHint", { count: activeOcCount })}</p>
              )}
            </div>
          )}

          {tab === "operation" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("order_period")}
              {field("order_days_description")}
              {field("order_day_specific")}
              {field("delivery_day_specific")}
              {field("attention_schedule", true)}
              {field("operations_start_date", true)}
            </div>
          )}

          {tab === "services" && isServ && (
            <div className="space-y-5">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("service_modality")}</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {MODALITIES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-testid={`edit-modality-${m}`}
                      onClick={() => set("service_modality", m)}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${
                        form.service_modality === m ? "border-[#FFDF00] bg-[#FFFBE6] text-[#1A1A1A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {t(`supplierReg.modalityOptions.${m}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("service_response_time")}</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {RESPONSE_TIMES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`edit-response-${r}`}
                      onClick={() => set("service_response_time", r)}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${
                        form.service_response_time === r ? "border-[#FFDF00] bg-[#FFFBE6] text-[#1A1A1A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {t(`supplierReg.responseTimeOptions.${r}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{lbl("service_description")}</label>
                <textarea
                  data-testid="edit-field-service_description"
                  value={form.service_description}
                  onChange={(e) => set("service_description", e.target.value)}
                  rows={3}
                  className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {toggleField("has_active_contract")}
              </div>
            </div>
          )}

          {tab === "branches" && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("supplierEdit.tab.branches")}</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {BRANCH_CODES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`edit-branch-${c}`}
                    onClick={() => toggleCode(c)}
                    className={`px-4 py-2 rounded-xl text-sm font-bold border transition ${
                      codes.includes(c) ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab === "branch_ops" && (
            <SupplierBranchOpsTab supplier={supplier} branches={branches} onUpdated={() => onSaved && onSaved()} />
          )}
        </div>

        {confirmFiscal && (
          <div data-testid="fiscal-confirm-banner" className="mx-6 mb-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{t("supplierEdit.fiscal.confirmActiveOcs", { count: activeOcCount })}</span>
          </div>
        )}
        <div className="flex gap-2 px-6 py-4 border-t border-slate-100">
          <button data-testid="supplier-edit-cancel" onClick={() => (confirmFiscal ? setConfirmFiscal(false) : onClose())} disabled={busy}
            className="flex-1 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
            {t("supplierEdit.cancelButton")}
          </button>
          <button data-testid="supplier-edit-save" onClick={save} disabled={busy || !hasChanges}
            className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            <Save className="w-4 h-4" /> {confirmFiscal ? t("common.confirm") : t("supplierEdit.saveButton")}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
